/** The corners along the bottom of a distance chart.
 *
 * Every chart on the Sessions screen shares one axis, metres along the
 * reference lap, and a reader going from the delta trace to the pedals to
 * the corner table needs the same landmarks on each. So this is one row,
 * drawn under any of them: a tick at each apex and the corner's number where
 * there is room for it. Where corners crowd — a chicane, an infield — the
 * ticks stay and the numbers thin out, which is what `cornerTicks` decides,
 * so the row never prints two numbers on top of each other.
 */

import type { CornerTick } from '@/components/sessions/compare';

export interface CornerAxisProps {
  ticks: readonly CornerTick[];
  /** The chart's drawing width, which the ticks' `x` is in. */
  width: number;
}

export function CornerAxis({ ticks, width }: CornerAxisProps) {
  if (ticks.length === 0) return null;
  return (
    <div
      aria-hidden="true"
      className="tabular-nums"
      style={{
        position: 'relative',
        height: 14,
        fontSize: 9.5,
        color: 'var(--color-neutral-500)',
        lineHeight: '14px',
      }}
    >
      {ticks.map((tick) => (
        <span
          key={tick.n}
          style={{
            position: 'absolute',
            left: `${(tick.x / width) * 100}%`,
            top: 0,
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          {tick.labelled ? `T${tick.n}` : '·'}
        </span>
      ))}
    </div>
  );
}

/** Faint verticals at the apexes, inside a chart's own SVG. */
export function CornerLines({ ticks, height }: { ticks: readonly CornerTick[]; height: number }) {
  return (
    <>
      {ticks.map((tick) => (
        <line
          key={tick.n}
          x1={tick.x}
          y1={0}
          x2={tick.x}
          y2={height}
          stroke="var(--color-divider)"
          strokeOpacity={0.7}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </>
  );
}

export default CornerAxis;
